
import React from 'react';
import { getSubScores } from './utils';
import { DeepAnalysisResult } from '@/types/analysisTypes';
import { AlertCircle } from 'lucide-react';

interface DetailedScoresListProps {
  pageAnalysisResult: DeepAnalysisResult;
}

export const DetailedScoresList: React.FC<DetailedScoresListProps> = ({
  pageAnalysisResult
}) => {
  // Safety check - if pageAnalysisResult is null/undefined, provide default
  if (!pageAnalysisResult) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        No analysis data available
      </div>
    );
  }

  const scores = getSubScores(pageAnalysisResult);
  const hasAllZeroScores = scores.every(score => score.score === 0);
  const hasError = pageAnalysisResult.error !== undefined;
  
  if (scores.length === 0) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        No detailed scores available
      </div>
    );
  }
  
  if (hasAllZeroScores || hasError) {
    return (
      <div className="p-6 space-y-4">
        <div className="flex items-start gap-3 p-4 border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
          <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
          <div className="space-y-2">
            <h5 className="font-medium text-amber-700 dark:text-amber-400">
              {hasError ? 'Analysis Error' : 'Analysis returned no scores'}
            </h5>
            <p className="text-sm text-amber-600 dark:text-amber-500">
              {hasError 
                ? pageAnalysisResult.error 
                : 'The page analysis operation completed but did not return any valid scores. This could be due to one of the following reasons:'}
            </p>
            
            {!hasError && (
              <>
                <ul className="text-sm text-amber-600 dark:text-amber-500 list-disc ml-4 space-y-1">
                  <li>API key validation issue</li>
                  <li>Response format mismatch</li>
                  <li>Content parsing error</li>
                  <li>Rate limiting or timeout</li>
                </ul>
                <p className="text-sm text-amber-600 dark:text-amber-500">
                  Please check your API settings and try again. If the issue persists, try a different page or model.
                </p>
              </>
            )}
          </div>
        </div>
        
        {/* Still display the zero score items */}
        <div className="space-y-4 mt-4">
          {scores.map((item, i) => (
            <div key={i} className="glass-panel p-4 rounded-lg opacity-75">
              <div className="flex justify-between items-center mb-2">
                <h5 className="font-medium">{item.title}</h5>
                <span className="font-medium">{item.score}/20</span>
              </div>
              <p className="text-sm text-muted-foreground">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {scores.map((item, i) => (
        <div key={i} className="glass-panel p-4 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <h5 className="font-medium">{item.title}</h5>
            <span className="font-medium">{item.score}/20</span>
          </div>
          <p className="text-sm text-muted-foreground">{item.description}</p>
        </div>
      ))}
    </div>
  );
};
